<template>
  <div id="profile-payment-details" class="content-pane">
    <h4 class="header-lined font-md wt-md mb-4">Get paid by A Perfect Space</h4>

    <form
      :class="{ loading }"
      @submit.prevent="saveBankAccountDetails"
      class="form-section mb-4"
    >
      <h3 class="wt-bold mb-2">Bank Details</h3>
      <div
        class="form-group vanilla"
        :class="{ 'has-input-error': errors.bank_account_name }"
      >
        <label for="bank_account_name"
          >Account Name <span class="required">*</span></label
        >
        <input
          id="bank_account_name"
          name="bank_account_name"
          maxlength="50"
          v-model="bank_account_details.bank_account_name"
          type="text"
          required
        />
        <input-error v-if="errors.bank_account_name">
          {{ errors.email[0] }}
        </input-error>
      </div>
      <div
        class="form-group vanilla"
        :class="{ 'has-input-error': errors.bank_account_bsb }"
      >
        <label for="bank_account_bsb"
          >BSB <span class="required">*</span></label
        >
        <input
          id="bank_account_bsb"
          name="bank_account_bsb"
          minlength="6"
          maxlength="6"
          v-model="bank_account_details.bank_account_bsb"
          type="text"
          required
        />
        <input-error v-if="errors.bank_account_bsb">
          {{ errors.bank_account_bsb[0] }}
        </input-error>
      </div>
      <div
        class="form-group vanilla"
        :class="{ 'has-input-error': errors.bank_account_number }"
      >
        <label for="bank_account_number"
          >Account Number <span class="required">*</span></label
        >
        <input
          id="bank_account_number"
          name="bank_account_number"
          minlength="6"
          maxlength="10"
          v-model="bank_account_details.bank_account_number"
          type="text"
          required
        />
        <input-error v-if="errors.bank_account_number">
          {{ errors.bank_account_number[0] }}
        </input-error>
      </div>
      <div
        class="form-group vanilla submit"
        v-if="showSaveBankAccountDetailsButton"
      >
        <loader-button
          type="submit"
          :loading="loading"
          label="Save Bank Account Details"
          class="mt-4"
        ></loader-button>
      </div>
    </form>
    <loader :loading="loading"></loader>
    <h4 class="header-lined"></h4>
    <div
      v-if="
        !$store.getters['user/stripeConnectAccount'] &&
        typeof $store.getters['account/current'].data.connect !== 'undefined'
      "
    >
      <div
        class="alert warning"
        v-if="$store.getters['account/current'].data.connect === 'refresh'"
      >
        <p>
          Your on-boarding process was canceled, please click the button below
          to continue connecting your Stripe account.
        </p>
      </div>
      <div
        class="alert warning"
        v-else-if="$store.getters['account/current'].data.connect === 'return'"
      >
        <p>
          Thanks for completing the on-boarding process, please wait while we
          verify your account.
        </p>
      </div>
    </div>
    <div v-if="initialized">
      <div v-if="!stripeConnectIsOnboarded">
        <div class="st">
          <h3>What is Stripe?</h3>
          <p>
            Stripe is an online credit card provider that has been audited by a
            PCI-certified auditor and is certified to PCI Service Provider Level
            1. We accept credit card payments through Stripe for all bookings.
            When a booking is confirmed for your listing, we will send you a
            quote to accept and payment will be sent to you online via Stripe.
          </p>
        </div>
        <p class="mb-10">
          A Perfect Space partners with Stripe for secure payments and financial
          services. Please complete the Stripe on boarding process to get paid
          into your bank account.
        </p>
        <button
          type="button"
          class="button loader-button danger"
          :class="{ loading }"
          :disabled="loading"
          @click="connectOnboardGo"
        >
          <svg-icon id="loading" class="spin"></svg-icon>
          Connect your Stripe account
        </button>
      </div>
      <div v-else id="stripe-connect-dashboard">
        <div class="st">
          <h3>What is Stripe?</h3>
          <p>
            Stripe is an online credit card provider that has been audited by a
            PCI-certified auditor and is certified to PCI Service Provider Level
            1. We accept credit card payments through Stripe for all bookings.
            When a booking is confirmed for your listing, we will send you a
            quote to accept and payment will be sent to you online via Stripe.
          </p>
        </div>
        <header>
          <div>
            <button
              type="button"
              class="button"
              :disabled="loading"
              @click="viewStripeDashboard(true)"
            >
              View Stripe account
            </button>
          </div>
          <div>
            <span class="label-like wt-sb">This month</span>
            <p class="font-md wt-bold mt-2 mb-1">
              {{ dashboard.balance.this_month.amount }}
            </p>
            <span class="fg-light-grey font-sm"
              >{{ dashboard.balance.this_month.count }} transactions</span
            >
          </div>
          <div>
            <span class="label-like wt-sb">Your Balance</span>
            <p class="font-md wt-bold mt-2 mb-1">
              {{ dashboard.balance.pending }}
            </p>
            <span class="fg-light-grey font-sm"
              >{{ dashboard.balance.available }} available</span
            >
          </div>
        </header>
        <section>
          <div
            v-if="
              typeof dashboard.transfers.data !== 'undefined' &&
              dashboard.transfers.data.length
            "
          >
            <div
              class="resource-table-container stripe-account-transfers-container"
            >
              <table
                class="resource-table not-interactive mobile-hidden stripe-account-transfers"
              >
                <thead>
                  <tr>
                    <th class="wt-bold">Booking #</th>
                    <th class="wt-bold">Description</th>
                    <th>&nbsp;</th>
                    <th class="wt-bold">Amount</th>
                    <th class="wt-bold">Date</th>
                    <th class="wt-bold">Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="transfer in dashboard.transfers.data">
                    <td>
                      <span>{{ transfer.metadata["Booking ID"] || "" }}</span>
                    </td>
                    <td>
                      <span>{{ transfer.description }}</span>
                    </td>
                    <td>
                      <span>{{ transfer.metadata["Payment part"] || "" }}</span>
                    </td>
                    <td>
                      <span>{{ transfer.amount_formatted }}</span>
                    </td>
                    <td>
                      <span>{{ transfer.created_date_formatted }}</span>
                    </td>
                    <td>
                      <span>{{ transfer.created_time_formatted }}</span>
                    </td>
                  </tr>
                </tbody>
                <tfoot v-if="false">
                  <tr>
                    <td class="pagination" colspan="4">
                      <!--                      <pagination :current-page="page" :total-pages="totalPages" @change-page="changePage"></pagination>-->
                    </td>
                  </tr>
                </tfoot>
              </table>

              <table
                class="resource-table production-listing mobile-table mobile-visible"
              >
                <tbody>
                  <template v-for="transfer in dashboard.transfers.data">
                    <tr>
                      <th>Amount</th>
                      <td>{{ transfer.amount_formatted }}</td>
                    </tr>
                    <tr>
                      <th>Description</th>
                      <td>{{ transfer.description }}</td>
                    </tr>
                    <tr>
                      <th>Date</th>
                      <td>{{ transfer.created_date_formatted }}</td>
                    </tr>
                    <tr>
                      <th>Time</th>
                      <td>{{ transfer.created_time_formatted }}</td>
                    </tr>
                  </template>
                </tbody>
                <tfoot v-if="false">
                  <tr>
                    <td class="pagination" colspan="4">
                      <!--                      <pagination :current-page="page" :total-pages="totalPages" @change-page="changePage"></pagination>-->
                    </td>
                  </tr>
                </tfoot>
              </table>
              <p class="mt-10 mb-16 lh-std">
                The charged amount, less any fees, is initially reflected your
                pending balance, and becomes available on a 2-day rolling
                basis.<br />
                Payouts are automatically remitted to your nominated bank
                account.
              </p>
            </div>
          </div>
          <div v-else class="alert warning">
            <p>You don't have any transactions yet.</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    redirectTo: {
      type: String,
      required: false,
      default: null,
    },
    showSaveBankAccountDetailsButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      initialized: false,
      dashboard: {
        balance: {
          available: null,
          pending: null,
          this_month: {
            amount: null,
            count: 0,
          },
        },
        transfers: null,
      },
      errors: [],
      bank_account_details: {
        bank_account_name: "",
        bank_account_bsb: "",
        bank_account_number: "",
      },
      stripeConnectIsOnboarded: false,
    };
  },
  mounted() {
    this.$store.dispatch("user/refreshUser");

    this.loading = true;
    axios
      .get("/api/account/bank-account-details")
      .then((response) => {
        this.bank_account_details = response.data;
      })
      .then(() => {
        this.loading = false;
      });
  },
  methods: {
    saveBankAccountDetails() {
      this.loading = true;
      this.errors = [];

      axios
        .put("/api/account/bank-account-details", this.bank_account_details)
        .then(() => {
          this.$store.dispatch("user/refreshUser");
          this.$toasted.show("Your profile has been updated.", {
            type: "success",
          });
          this.$store.commit("user/setBankAccountOnBoarded", true);
        })
        .catch((error) => {
          this.$toasted.show(
            "Oops! We couldn't update your bank details, please try again.",
            {
              type: "error",
            }
          );
          return (this.errors = error.response.data.errors);
        })
        .then(() => {
          this.loading = false;
        });
    },
    getConnectAccount() {
      this.loading = true;
      return axios
        .get("/api/account/stripe-connect/account")
        .then((response) => {
          this.$store.commit(
            "user/setStripeConnectAccount",
            response.data.account
          );

          this.stripeConnectIsOnboarded =
            !!response.data.account.details_submitted;
          this.dashboard.balance = response.data.balance;
          this.dashboard.transfers = response.data.transfers;
          this.$store.dispatch("user/refreshUser");
        })
        .catch(() => {
          this.$toasted.show("Could not fetch Stripe connect account", {
            type: "error",
          });
        })
        .then(() => {
          this.initialized = true;
          this.loading = false;
        });
    },
    viewStripeDashboard(account) {
      this.loading = true;
      axios
        .get("/api/account/stripe-connect/account/login-link")
        .then((response) => {
          if (response.data.url) {
            window.open(response.data.url + (account ? "#/account " : ""));
          } else {
            throw new Error("Invalid login link response");
          }
        })
        .catch(() => {
          this.$toasted.show(
            "Could not fetch your Stripe login link, please try again later",
            {
              type: "error",
            }
          );
        })
        .then(() => (this.loading = false));
    },
    connectOnboardGo() {
      this.loading = true;
      axios
        .get("/api/account/stripe-connect/account/onboard", {
          params: {
            redirect_to: this.redirectTo,
          },
        })
        .then((response) => {
          if (response.data.url) {
            this.loading = false;
            return window.open(response.data.url, "_blank").focus();
          }
          throw new Error("Invalid onboard link response");
        })
        .catch(() => {
          this.$toasted.show("Could not fetch Stripe connect onboard link", {
            type: "error",
          });
          this.loading = false;
        });
    },
    payoutBalance() {
      this.loading = true;
      axios
        .post("/api/account/stripe-connect/account/payout")
        .then(() => {
          this.getConnectAccount().then(() => {
            this.$toasted.show(
              "Your available balance payout has been processed",
              {
                type: "success",
              }
            );
          });
        })
        .catch((error) => {
          this.$toasted.show(error.response.data, {
            type: "error",
          });
          this.loading = false;
        });
    },
  },
  computed: {
    canPayout() {
      return (
        this.dashboard.balance.available && this.dashboard.balance.available > 0
      );
    },
  },
};
</script>
